import type { QueryClient } from '@tanstack/react-query';
import type { ConversationItem, ConversationView, Role, WsEvent } from '@chatapp-inbox/shared';
import {
  addMessageToCache,
  patchAttachmentInCache,
  patchMessageStatusInCache,
} from '../chat/messages-cache';
import { conversationBitrixKey, conversationKey, messagesKey, notesKey } from '../conversation';
import { applyConversationToLists, removeConversationFromLists, type Viewer } from './lists';

export interface ApplyContext {
  viewer: Viewer;
  /** Pede para refazer as listas em breve (várias chamadas seguidas viram uma só). */
  refreshLists: () => void;
  /**
   * Pede para refazer só os RESULTADOS DE BUSCA em breve. Eles não são remendados como as listas das abas
   * (uma conversa que muda pode passar a casar com a busca, ou deixar de casar): sem busca na tela isto
   * não pede nada ao servidor.
   */
  refreshSearches: () => void;
}

/** O evento traz a conversa SEM `canWrite` (é por pessoa): calcula como o servidor calcula. */
export function toConversationItem(
  view: ConversationView,
  viewer: { id: string; role: Role },
): ConversationItem {
  return { ...view, canWrite: viewer.role === 'admin' || view.assignedTo?.id === viewer.id };
}

/**
 * Aplica um evento do tempo real aos caches (conversa aberta, mensagens, listas). Idempotente: o
 * mesmo evento duas vezes (retomada que se sobrepõe, evento que chega junto do resultado de uma
 * ação da própria pessoa) deixa o cache igual. NUNCA cria cache de dados que ninguém carregou:
 * depois de um logout isso poria dado de outra sessão na tela da pessoa seguinte.
 */
export function applyEvent(queryClient: QueryClient, event: WsEvent, ctx: ApplyContext): void {
  switch (event.type) {
    case 'message.created': {
      // Não cria o cache de mensagens: se a conversa não foi aberta, o histórico vem do servidor.
      addMessageToCache(queryClient, event.message.conversationId, event.message);
      // Mensagem do cliente: o Bitrix confere (ou cria) o Lead dele em segundos. Só quem tem o bloco aberto
      // pede de novo; sem consulta feita, não há nada a invalidar.
      if (event.message.direction === 'in') {
        void queryClient.invalidateQueries({
          queryKey: conversationBitrixKey(event.message.conversationId),
        });
      }
      return;
    }
    case 'message.status': {
      patchMessageStatusInCache(queryClient, event.conversationId, event.messageId, {
        status: event.status,
        errorCode: event.errorCode,
        errorDetail: event.errorDetail ?? null,
      });
      // A prévia da lista mostra "Falha no envio": uma falha nova precisa chegar até lá. (A lista
      // não sabe qual mensagem é a última, então em vez de adivinhar ela é refeita em breve.)
      if (event.status === 'error') ctx.refreshLists();
      return;
    }
    case 'attachment.updated': {
      // O arquivo recebido terminou de baixar (ou falhou): a bolha deixa de dizer "Baixando…".
      patchAttachmentInCache(queryClient, event.conversationId, event.messageId, event.attachment);
      return;
    }
    case 'conversation.updated': {
      const item = toConversationItem(event.conversation, ctx.viewer);
      // Conversa aberta: atualiza no lugar (responsável, `canWrite`, não lidas), mas só se existir.
      if (queryClient.getQueryData(conversationKey(item.id))) {
        queryClient.setQueryData(conversationKey(item.id), item);
      }
      if (applyConversationToLists(queryClient, item, ctx.viewer).needsRefresh) ctx.refreshLists();
      ctx.refreshSearches();
      return;
    }
    case 'conversation.removed': {
      removeConversationFromLists(queryClient, event.conversationId);
      ctx.refreshSearches();
      // Quem está com ela aberta passa a ver "não encontrada" (a busca dá 404); o que ficou em
      // cache de histórico e notas não é mais para ela: sai o que ninguém está usando.
      if (queryClient.getQueryData(conversationKey(event.conversationId))) {
        void queryClient.invalidateQueries({ queryKey: conversationKey(event.conversationId) });
      }
      queryClient.removeQueries({ queryKey: messagesKey(event.conversationId), type: 'inactive' });
      queryClient.removeQueries({ queryKey: notesKey(event.conversationId), type: 'inactive' });
      return;
    }
  }
}
